import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Modal from '../Modal';
import DocArticle from './DocArticle';
import DocPermissions from './DocPermissions';
import { DocIcon, SPACE_ICONS } from './DocIcons';
import { useMe } from '../../lib/me';
import { docsApi, errorOf, useDocsMe, refreshDocsMe, when, TYPES, TYPE_LABEL, TYPE_BLURB, TEMPLATE } from '../../lib/docs';
import './Docs.css';

/**
 * Documentation (migration 083): the IT department's runbooks, how-tos,
 * workflows and reference pages, for staff - and for contractors exactly as
 * far as an administrator has decided (Settings -> Documentation, and the
 * Permissions button on every space, folder and article).
 *
 *   /docs              home: search, the spaces, recently updated, most
 *                      read, bookmarks, my drafts and reviews
 *   /docs?q=&tag=      search
 *   /docs?view=reviews articles of mine due a review (where the bell sends)
 *   /docs/:id          a space or folder (what is in it), or an article
 *   /docs/:id?mode=edit|history
 *
 * The same page for every role. What is in it is the server's answer for
 * this person; nothing here decides access.
 */

const EXPANDED_KEY = 'jv-docs-open';
const readOpen = () => { try { return new Set(JSON.parse(localStorage.getItem(EXPANDED_KEY) || '[]')); } catch { return new Set(); } };
/** Never opened the tree in this browser: every space starts open, so the first look shows what is there. */
const firstVisit = () => { try { return localStorage.getItem(EXPANDED_KEY) === null; } catch { return true; } };

function TreeNode({ n, depth, openSet, toggle, current, onPick }) {
  const isOpen = openSet.has(n.id);
  const kids = n.children || [];
  const icon = n.kind === 'space' ? (n.icon || 'book') : n.kind === 'folder' ? 'folder' : n.article_type;
  return (
    <li className={`dt-item dt-${n.kind}`} role="treeitem" aria-expanded={n.kind !== 'article' ? isOpen : undefined} aria-selected={current === n.id}>
      <div className={`dt-row${current === n.id ? ' is-current' : ''}${n.path_only ? ' is-path' : ''}`} style={{ paddingLeft: `${0.35 + depth * 0.85}rem` }}>
        {n.kind !== 'article' ? (
          <button type="button" className={`dt-twisty${isOpen ? ' is-open' : ''}`} onClick={() => toggle(n.id)}
                  aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${n.title}`} disabled={!kids.length}>
            <DocIcon name="chevron" />
          </button>
        ) : <span className="dt-twisty-gap" aria-hidden="true" />}
        <Link to={`/docs/${n.id}`} className="dt-link" onClick={onPick} aria-current={current === n.id ? 'page' : undefined}>
          <DocIcon name={icon} />
          <span className="dt-title">{n.title}</span>
          {n.status === 'draft' && <span className="dt-draft" title="Draft">Draft</span>}
          {n.kind !== 'article' && n.inherits === false && !n.path_only && <span className="dt-lock" title="Has its own permissions"><DocIcon name="lock" /></span>}
        </Link>
      </div>
      {n.kind !== 'article' && isOpen && kids.length > 0 && (
        <ul role="group">
          {kids.map((c) => <TreeNode key={c.id} n={c} depth={depth + 1} openSet={openSet} toggle={toggle} current={current} onPick={onPick} />)}
        </ul>
      )}
    </li>
  );
}

function ArticleCard({ a, extra }) {
  return (
    <li>
      <Link to={`/docs/${a.id}`} className="dc-card">
        <span className={`dc-ico dc-ico-${a.article_type}`}><DocIcon name={a.article_type} /></span>
        <span className="dc-text">
          <span className="dc-title">{a.title}</span>
          <span className="dc-sub">{[TYPE_LABEL[a.article_type] || a.type_label, ...(a.path || [])].join(' · ')}</span>
          {extra && <span className="dc-extra">{extra}</span>}
        </span>
        {a.status === 'draft' && <span className="dt-draft">Draft</span>}
        {a.status === 'changed' && <span className="dt-draft dt-changed">Changed</span>}
      </Link>
    </li>
  );
}

function Section({ title, icon, children, empty }) {
  return (
    <section className="dh-sec">
      <h2 className="dh-sec-title"><DocIcon name={icon} />{title}</h2>
      {children || <p className="dp-hint">{empty}</p>}
    </section>
  );
}

/** Text with «marks» around the words searched for, as <mark> elements. */
function Marked({ text }) {
  const parts = String(text || '').split(/(«[^»]*»)/g);
  return parts.map((p, i) => (p.startsWith('«') ? <mark key={i}>{p.slice(1, -1)}</mark> : <React.Fragment key={i}>{p}</React.Fragment>));
}

function SearchResults({ params, spaces }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const q = params.get('q') || '';
  const tag = params.get('tag') || '';
  const type = params.get('type') || '';
  const space = params.get('space') || '';
  const navigate = useNavigate();
  useEffect(() => {
    let live = true;
    setRes(null); setErr(null);
    docsApi.get('/docs/search', { q: q || undefined, tag: tag || undefined, type: type || undefined, space: space || undefined })
      .then((r) => { if (live) setRes(r); }).catch((e) => { if (live) setErr(errorOf(e)); });
    return () => { live = false; };
  }, [q, tag, type, space]);
  const set = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); navigate(`/docs?${p}`); };
  return (
    <div className="ds">
      <header className="ds-head">
        <h1 className="dp-page-title">{q ? <>Results for “{q}”</> : tag ? <>Tagged #{tag}</> : 'Search'}</h1>
        <div className="ds-filters" role="group" aria-label="Filter">
          <select className="dp-select" aria-label="Type" value={type} onChange={(e) => set('type', e.target.value)}>
            <option value="">Every type</option>
            {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
          <select className="dp-select" aria-label="Space" value={space} onChange={(e) => set('space', e.target.value)}>
            <option value="">Every space</option>
            {spaces.filter((s) => !s.path_only).map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          {tag && <button type="button" className="da-tag is-x" onClick={() => set('tag', '')}>#{tag} <DocIcon name="close" /></button>}
        </div>
      </header>
      {err && <p className="dp-error" role="alert">{err}</p>}
      {!res && !err && <p className="dp-hint" aria-busy="true">Searching…</p>}
      {res && (
        <>
          {res.places?.length > 0 && (
            <ul className="ds-places">
              {res.places.map((p) => (
                <li key={p.id}><Link to={`/docs/${p.id}`} className="ds-place"><DocIcon name={p.kind === 'space' ? (p.icon || 'book') : 'folder'} />{p.title}{p.path?.length ? <small>{p.path.join(' › ')}</small> : null}</Link></li>
              ))}
            </ul>
          )}
          <p className="ds-count" role="status">{res.results.length ? `${res.results.length} article${res.results.length === 1 ? '' : 's'}` : 'Nothing found. Try fewer or different words.'}</p>
          <ol className="ds-list">
            {res.results.map((r) => (
              <li key={r.id}>
                <Link to={`/docs/${r.id}`} className="ds-hit">
                  <span className="ds-hit-head">
                    <span className={`dc-ico dc-ico-${r.article_type}`}><DocIcon name={r.article_type} /></span>
                    <span className="ds-hit-title">{r.title}</span>
                    {r.status === 'draft' && <span className="dt-draft">Draft</span>}
                  </span>
                  <span className="ds-hit-path">{[TYPE_LABEL[r.article_type] || r.type_label, ...(r.path || [])].join(' › ')} · updated {when(r.updated_at)}</span>
                  {r.snippet && <span className="ds-hit-snip"><Marked text={r.snippet} /></span>}
                  {r.tags?.length > 0 && <span className="ds-hit-tags">{r.tags.map((t) => `#${t}`).join('  ')}</span>}
                </Link>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

function Home({ tree, me, docsMe, reviewsOnly, onNewSpace }) {
  const [home, setHome] = useState(null);
  const [tags, setTags] = useState([]);
  useEffect(() => {
    let live = true;
    docsApi.get('/docs/home').then((r) => { if (live) setHome(r); }).catch(() => { if (live) setHome({ recent: [], popular: [], favourites: [], reviews: [], drafts: [] }); });
    docsApi.get('/docs/tags').then((r) => { if (live) setTags(r.tags.slice(0, 24)); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const spaces = tree?.spaces || [];
  if (reviewsOnly) {
    return (
      <div className="dhm">
        <h1 className="dp-page-title">Due for review</h1>
        <p className="dp-lead">Articles you own that are due a check that they are still right. Open one and press <strong>Still correct</strong>, or edit it.</p>
        {home?.reviews?.length ? <ul className="dc-list">{home.reviews.map((a) => <ArticleCard key={a.id} a={a} />)}</ul>
          : <p className="dp-hint">{home ? 'Nothing is due. Thank you.' : 'Loading…'}</p>}
      </div>
    );
  }
  const nothing = home && !spaces.length;
  return (
    <div className="dhm">
      <header className="dhm-hero">
        <div>
          <h1 className="dp-page-title">Documentation</h1>
          <p className="dp-lead">{me?.kind === 'contractor' || docsMe?.who === 'contractor'
            ? 'The runbooks, how-tos and workflows NCMB IT has shared with you.'
            : 'How NCMB’s IT is set up, how to fix it, and who does what - runbooks, how-tos, workflows and reference.'}</p>
        </div>
        {tree?.can_create_space && <button type="button" className="btn btn-primary" onClick={onNewSpace}><DocIcon name="plus" />New space</button>}
      </header>
      {nothing && (
        <div className="dhm-empty" role="status">
          <DocIcon name="book" />
          <h2>Nothing here for you yet</h2>
          <p>{tree?.can_create_space ? 'Add the first space - for example “Network & Wi-Fi” or “Microsoft 365”.' : 'When NCMB IT shares documentation with you, it will appear here.'}</p>
        </div>
      )}
      {spaces.length > 0 && (
        <section className="dh-sec">
          <h2 className="dh-sec-title"><DocIcon name="book" />Spaces</h2>
          <ul className="dsp-grid">
            {spaces.map((s) => {
              const count = (function c(n) { return (n.children || []).reduce((t, k) => t + (k.kind === 'article' ? 1 : c(k)), 0); }(s));
              return (
                <li key={s.id}>
                  <Link to={`/docs/${s.id}`} className="dsp-card">
                    <span className="dsp-ico"><DocIcon name={s.icon || 'book'} /></span>
                    <span className="dsp-title">{s.title}</span>
                    {s.description && !s.path_only && <span className="dsp-desc">{s.description}</span>}
                    <span className="dsp-meta">{count} article{count === 1 ? '' : 's'}{s.inherits === false && !s.path_only ? ' · own permissions' : ''}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {home?.reviews?.length > 0 && (
        <Section title="Due for review" icon="clock"><ul className="dc-list">{home.reviews.map((a) => <ArticleCard key={a.id} a={a} />)}</ul></Section>
      )}
      <div className="dhm-cols">
        {home && home.recent.length > 0 && (
          <Section title="Recently updated" icon="history">
            <ul className="dc-list">{home.recent.map((a) => <ArticleCard key={a.id} a={a} extra={`${when(a.updated_at)}${a.author_name ? ` · ${a.author_name}` : ''}`} />)}</ul>
          </Section>
        )}
        <div>
          {home?.favourites?.length > 0 && (
            <Section title="Your bookmarks" icon="bookmark"><ul className="dc-list">{home.favourites.map((a) => <ArticleCard key={a.id} a={a} />)}</ul></Section>
          )}
          {home?.popular?.length > 0 && (
            <Section title="Most read this month" icon="eye"><ul className="dc-list">{home.popular.map((a) => <ArticleCard key={a.id} a={a} extra={`${a.views} view${a.views === 1 ? '' : 's'}`} />)}</ul></Section>
          )}
          {home?.drafts?.length > 0 && (
            <Section title="Drafts and unpublished changes" icon="edit"><ul className="dc-list">{home.drafts.map((a) => <ArticleCard key={a.id} a={a} />)}</ul></Section>
          )}
          {tags.length > 0 && (
            <Section title="Tags" icon="link">
              <ul className="da-tags">{tags.map((t) => <li key={t.tag}><Link className="da-tag" to={`/docs?tag=${encodeURIComponent(t.tag)}`}>#{t.tag} <small>{t.count}</small></Link></li>)}</ul>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function NewArticle({ parent, onClose, onMade }) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('runbook');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await docsApi.post('/docs/nodes', { kind: 'article', parent_id: parent.id, title, article_type: type, body: TEMPLATE[type] });
      onMade(r.node.id);
    } catch (e) { setErr(errorOf(e)); setBusy(false); }
  };
  return (
    <Modal title="New article" subtitle={`In “${parent.title}”. It starts as a draft only editors see.`} onClose={onClose}
           footer={<><button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" disabled={!title.trim() || busy} onClick={make}>Create and write</button></>}>
      <label className="dp-label" htmlFor="dn-title">Title</label>
      <input id="dn-title" className="de-input" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Reset a user’s MFA"
             onKeyDown={(e) => { if (e.key === 'Enter' && title.trim()) make(); }} autoFocus />
      <span className="dp-label">Kind of article</span>
      <div className="dn-types" role="radiogroup" aria-label="Kind of article">
        {TYPES.map((t) => (
          <button key={t} type="button" role="radio" aria-checked={type === t} className={`dn-type${type === t ? ' is-on' : ''}`} onClick={() => setType(t)}>
            <DocIcon name={t} /><strong>{TYPE_LABEL[t]}</strong><small>{TYPE_BLURB[t]}</small>
          </button>
        ))}
      </div>
      {err && <p className="dp-error" role="alert">{err}</p>}
    </Modal>
  );
}

function NewSpace({ onClose, onMade }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('book');
  const [err, setErr] = useState(null);
  const make = async () => {
    try { const r = await docsApi.post('/docs/nodes', { kind: 'space', title, description, icon }); onMade(r.node.id); } catch (e) { setErr(errorOf(e)); }
  };
  return (
    <Modal title="New space" subtitle="A space starts with the firm-wide default permissions. Change them with Permissions." onClose={onClose}
           footer={<><button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" disabled={!title.trim()} onClick={make}>Create space</button></>}>
      <label className="dp-label" htmlFor="ns-title">Name</label>
      <input id="ns-title" className="de-input" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Network & Wi-Fi" autoFocus />
      <label className="dp-label" htmlFor="ns-desc">What is in it</label>
      <input id="ns-desc" className="de-input" value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} placeholder="Switches, access points, the firewall" />
      <span className="dp-label">Icon</span>
      <div className="ns-icons" role="radiogroup" aria-label="Icon">
        {SPACE_ICONS.map((i) => (
          <button key={i} type="button" role="radio" aria-checked={icon === i} aria-label={i} className={`ns-icon${icon === i ? ' is-on' : ''}`} onClick={() => setIcon(i)}><DocIcon name={i} /></button>
        ))}
      </div>
      {err && <p className="dp-error" role="alert">{err}</p>}
    </Modal>
  );
}

function FolderView({ data, reload, onTree }) {
  const navigate = useNavigate();
  const { node, children, can, breadcrumbs } = data;
  const [newArticle, setNewArticle] = useState(false);
  const [folderName, setFolderName] = useState(null);
  const [share, setShare] = useState(false);
  const [rename, setRename] = useState(null);
  const [askArchive, setAskArchive] = useState(false);
  const [err, setErr] = useState(null);
  const run = async (fn) => { setErr(null); try { await fn(); reload(); onTree(); } catch (e) { setErr(errorOf(e)); } };
  if (node.path_only) {
    return (
      <div className="df">
        <h1 className="dp-page-title"><DocIcon name={node.kind === 'space' ? (node.icon || 'book') : 'folder'} />{node.title}</h1>
        <p className="dp-hint">You have been given some of what is in here:</p>
        <ul className="dc-list">{children.map((c) => (c.kind === 'article' ? <ArticleCard key={c.id} a={c} /> : (
          <li key={c.id}><Link to={`/docs/${c.id}`} className="dc-card"><span className="dc-ico"><DocIcon name="folder" /></span><span className="dc-text"><span className="dc-title">{c.title}</span></span></Link></li>
        )))}</ul>
      </div>
    );
  }
  const folders = children.filter((c) => c.kind === 'folder');
  const articles = children.filter((c) => c.kind === 'article');
  return (
    <div className="df">
      <header className="df-head">
        <div className="df-titles">
          <span className="df-kind">{node.kind === 'space' ? 'Space' : 'Folder'}{node.inherits === false && <> · <DocIcon name="lock" />own permissions</>}</span>
          {rename !== null ? (
            <div className="da-row">
              <input className="de-input" value={rename} onChange={(e) => setRename(e.target.value)} aria-label="Name" autoFocus
                     onKeyDown={(e) => { if (e.key === 'Enter') run(() => docsApi.patch(`/docs/nodes/${node.id}`, { title: rename })).then(() => setRename(null)); if (e.key === 'Escape') setRename(null); }} />
              <button type="button" className="btn btn-primary btn-small" onClick={() => run(() => docsApi.patch(`/docs/nodes/${node.id}`, { title: rename })).then(() => setRename(null))}>Save</button>
              <button type="button" className="btn btn-plain btn-small" onClick={() => setRename(null)}>Cancel</button>
            </div>
          ) : <h1 className="dp-page-title"><DocIcon name={node.kind === 'space' ? (node.icon || 'book') : 'folder'} />{node.title}</h1>}
          {node.description && <p className="dp-lead">{node.description}</p>}
        </div>
        <div className="da-actions" role="toolbar" aria-label={`${node.kind} actions`}>
          {can.edit && <button type="button" className="btn btn-primary btn-small" onClick={() => setNewArticle(true)}><DocIcon name="plus" />New article</button>}
          {can.edit && <button type="button" className="btn btn-outline btn-small" onClick={() => setFolderName('')}><DocIcon name="folder" />New folder</button>}
          {can.manage && <button type="button" className="btn btn-outline btn-small" onClick={() => setShare(true)}><DocIcon name="share" />Permissions</button>}
          {can.edit && rename === null && <button type="button" className="btn btn-plain btn-small" onClick={() => setRename(node.title)}><DocIcon name="edit" />Rename</button>}
          {can.delete && !askArchive && <button type="button" className="btn btn-plain btn-small" onClick={() => setAskArchive(true)}><DocIcon name="trash" />Archive</button>}
          {askArchive && (
            <span className="da-ask" role="group" aria-label="Archive">
              Archive this {node.kind} and everything in it?
              <button type="button" className="btn btn-danger btn-small" onClick={() => run(() => docsApi.del(`/docs/nodes/${node.id}`)).then(() => navigate(breadcrumbs.length > 1 ? `/docs/${breadcrumbs[breadcrumbs.length - 2].id}` : '/docs'))}>Archive</button>
              <button type="button" className="btn btn-plain btn-small" onClick={() => setAskArchive(false)}>Keep</button>
            </span>
          )}
        </div>
      </header>
      {folderName !== null && (
        <div className="df-newfolder da-row">
          <input className="de-input" value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="Folder name" aria-label="Folder name" autoFocus
                 onKeyDown={(e) => { if (e.key === 'Enter' && folderName.trim()) run(() => docsApi.post('/docs/nodes', { kind: 'folder', parent_id: node.id, title: folderName })).then(() => setFolderName(null)); if (e.key === 'Escape') setFolderName(null); }} />
          <button type="button" className="btn btn-primary btn-small" disabled={!folderName.trim()} onClick={() => run(() => docsApi.post('/docs/nodes', { kind: 'folder', parent_id: node.id, title: folderName })).then(() => setFolderName(null))}>Add folder</button>
          <button type="button" className="btn btn-plain btn-small" onClick={() => setFolderName(null)}>Cancel</button>
        </div>
      )}
      {err && <p className="dp-error" role="alert">{err}</p>}
      {!children.length && <div className="dhm-empty"><DocIcon name="folder" /><h2>Empty</h2><p>{can.edit ? 'Add the first article or folder.' : 'Nothing has been written here yet.'}</p></div>}
      {folders.length > 0 && (
        <ul className="dsp-grid df-folders">
          {folders.map((f) => (
            <li key={f.id}><Link to={`/docs/${f.id}`} className="dsp-card is-folder"><span className="dsp-ico"><DocIcon name="folder" /></span><span className="dsp-title">{f.title}</span>
              {f.inherits === false && !f.path_only && <span className="dsp-meta"><DocIcon name="lock" /> own permissions</span>}</Link></li>
          ))}
        </ul>
      )}
      {articles.length > 0 && <ul className="dc-list">{articles.map((a) => <ArticleCard key={a.id} a={a} />)}</ul>}
      {newArticle && <NewArticle parent={node} onClose={() => setNewArticle(false)} onMade={(id) => { onTree(); navigate(`/docs/${id}?mode=edit`); }} />}
      {share && (
        <Modal wide title={`Who can see “${node.title}”`} subtitle={`Permissions on this ${node.kind}, and everything in it that inherits them.`} onClose={() => { setShare(false); reload(); onTree(); }}>
          <DocPermissions nodeId={node.id} kind={node.kind} />
        </Modal>
      )}
    </div>
  );
}

function NodeView({ id, me, onTree, mode, setMode }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => docsApi.get(`/docs/nodes/${id}`)
    .then((r) => { setData(r); setErr(null); })
    .catch((e) => setErr(e?.response?.status === 404 ? 'missing' : errorOf(e))), [id]);
  useEffect(() => { setData(null); setErr(null); load(); }, [load]);
  if (err === 'missing') {
    return (
      <div className="dhm-empty" role="status">
        <DocIcon name="lock" /><h2>Not available</h2>
        <p>This page does not exist, or it has not been shared with you.</p>
        <Link to="/docs" className="btn btn-secondary">Back to Documentation</Link>
      </div>
    );
  }
  if (err) return <p className="dp-error" role="alert">{err}</p>;
  if (!data) return <p className="dp-hint" aria-busy="true">Loading…</p>;
  return (
    <>
      <nav className="db-crumbs" aria-label="Breadcrumb">
        <ol>
          <li><Link to="/docs">Docs</Link></li>
          {data.breadcrumbs.slice(0, -1).map((b) => <li key={b.id}><Link to={`/docs/${b.id}`}>{b.title}</Link></li>)}
          <li aria-current="page">{data.breadcrumbs[data.breadcrumbs.length - 1]?.title}</li>
        </ol>
      </nav>
      {data.node.kind === 'article'
        ? <DocArticle data={data} mode={mode} setMode={setMode} reload={() => { load(); onTree(); }} me={me ? { userId: me.id } : null}
                      onArchived={(parentId) => { onTree(); navigate(parentId ? `/docs/${parentId}` : '/docs'); }} />
        : <FolderView data={data} reload={load} onTree={onTree} />}
    </>
  );
}

export default function DocsPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const me = useMe();
  const docsMe = useDocsMe();
  const [tree, setTree] = useState(null);
  const [treeErr, setTreeErr] = useState(null);
  const [openSet, setOpenSet] = useState(readOpen);
  const [drawer, setDrawer] = useState(false);
  const [newSpace, setNewSpace] = useState(false);
  const [q, setQ] = useState(params.get('q') || '');
  const mode = ['edit', 'history'].includes(params.get('mode')) ? params.get('mode') : 'read';
  const setMode = (m) => { const p = new URLSearchParams(params); if (m === 'read') p.delete('mode'); else p.set('mode', m); setParams(p); };

  const loadTree = useCallback(() => docsApi.get('/docs/tree').then((r) => { setTree(r); setTreeErr(null); })
    .catch((e) => setTreeErr(e?.response?.status === 503 ? 'not-ready' : errorOf(e))), []);
  useEffect(() => { if (docsMe?.ready) loadTree(); }, [docsMe?.ready, loadTree]);
  useEffect(() => { setQ(params.get('q') || ''); }, [params]);
  useEffect(() => { setDrawer(false); }, [location.pathname]);

  // The current node's ancestors open in the tree.
  const parents = useMemo(() => {
    const map = new Map();
    const walk = (n, up) => { map.set(n.id, up); (n.children || []).forEach((c) => walk(c, [...up, n.id])); };
    (tree?.spaces || []).forEach((s) => walk(s, []));
    return map;
  }, [tree]);
  useEffect(() => {
    if (!id || !parents.has(id)) return;
    setOpenSet((prev) => {
      const next = new Set(prev);
      [...parents.get(id), id].forEach((x) => next.add(x));
      return next;
    });
  }, [id, parents]);
  const [fresh] = useState(firstVisit);
  useEffect(() => {
    if (!fresh || !tree) return;
    // A small tree opens all the way (a contractor's few shared pages are
    // seen at once); a big one opens its spaces.
    const containers = [];
    const walk = (n) => { if (n.kind !== 'article') { containers.push(n.id); (n.children || []).forEach(walk); } };
    tree.spaces.forEach(walk);
    setOpenSet((prev) => new Set([...prev, ...(containers.length <= 40 ? containers : tree.spaces.map((sp) => sp.id))]));
  }, [fresh, tree]);
  useEffect(() => { if (tree) { try { localStorage.setItem(EXPANDED_KEY, JSON.stringify([...openSet])); } catch { /* fine */ } } }, [openSet, tree]);
  const toggle = (nid) => setOpenSet((prev) => { const n = new Set(prev); if (n.has(nid)) n.delete(nid); else n.add(nid); return n; });

  const search = (e) => { e.preventDefault(); if (q.trim()) navigate(`/docs?q=${encodeURIComponent(q.trim())}`); };

  if (!docsMe) return <div className="container"><p className="dp-hint" aria-busy="true">Loading…</p></div>;
  if (!docsMe.ready || treeErr === 'not-ready') {
    return (
      <div className="container">
        <section className="dhm-empty docs-notready" role="status">
          <DocIcon name="book" />
          <h1>Documentation is not set up yet</h1>
          {docsMe.is_admin || me?.isAdmin
            ? <p>The server has the new code but not its database tables. Run <code>npm run migrate:083</code> on the server (migration 083_docs.sql) and reload this page.</p>
            : <p>It will be here once IT has finished setting it up. Nothing is lost - try again later.</p>}
        </section>
      </div>
    );
  }

  const searching = !id && (params.get('q') || params.get('tag') || params.get('type'));
  const reviews = !id && params.get('view') === 'reviews';

  return (
    <div className={`docs${mode === 'edit' ? ' is-editing' : ''}`}>
      <button type="button" className="docs-browse" aria-expanded={drawer} aria-controls="docs-side" onClick={() => setDrawer((v) => !v)}>
        <DocIcon name={drawer ? 'close' : 'menu'} />{drawer ? 'Close' : 'Browse docs'}
      </button>
      <aside id="docs-side" className={`docs-side${drawer ? ' is-open' : ''}`} aria-label="Documentation">
        <form className="docs-search" role="search" onSubmit={search}>
          <DocIcon name="search" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search docs" aria-label="Search documentation" />
        </form>
        <nav className="docs-quick" aria-label="Documentation pages">
          <NavLink end to="/docs" className={({ isActive }) => `docs-quick-link${isActive && !searching && !reviews ? ' is-on' : ''}`}><DocIcon name="home" />Home</NavLink>
          {docsMe.reviews_due > 0 && <Link to="/docs?view=reviews" className={`docs-quick-link${reviews ? ' is-on' : ''}`}><DocIcon name="clock" />Due for review <span className="docs-count">{docsMe.reviews_due}</span></Link>}
          {tree?.is_admin && <Link to="/settings/content/documentation" className="docs-quick-link"><DocIcon name="settings" />Settings</Link>}
        </nav>
        <div className="docs-tree-head">
          <span>Spaces</span>
          {tree?.can_create_space && <button type="button" className="docs-add" onClick={() => setNewSpace(true)} aria-label="New space"><DocIcon name="plus" /></button>}
        </div>
        {treeErr && <p className="dp-error" role="alert">{treeErr}</p>}
        {!tree && !treeErr && <p className="dp-hint" aria-busy="true">Loading…</p>}
        {tree && (
          <ul className="docs-tree" role="tree" aria-label="Spaces, folders and articles">
            {tree.spaces.map((s) => <TreeNode key={s.id} n={s} depth={0} openSet={openSet} toggle={toggle} current={id} onPick={() => setDrawer(false)} />)}
            {!tree.spaces.length && <li className="dp-hint">Nothing shared with you yet.</li>}
          </ul>
        )}
      </aside>
      <main className="docs-main">
        {id ? <NodeView key={id} id={id} me={me} onTree={loadTree} mode={mode} setMode={setMode} />
          : searching ? <SearchResults params={params} spaces={tree?.spaces || []} />
            : <Home tree={tree} me={me} docsMe={docsMe} reviewsOnly={reviews} onNewSpace={() => setNewSpace(true)} />}
      </main>
      {newSpace && <NewSpace onClose={() => setNewSpace(false)} onMade={(nid) => { setNewSpace(false); loadTree(); refreshDocsMe(); navigate(`/docs/${nid}`); }} />}
    </div>
  );
}
